// news-slider
import { onDestroy, onMount } from "@/modules/_";
import gsap, { Draggable, reduced } from "@lib/gsap";
import { Resize } from "@lib/subs";

const TWEEN_DURATION = 0.7;
const TWEEN_EASE = "expo.out";
const SNAP_DURATION = 0.4;
const DRAG_RESISTANCE = 0.05;
const THROW_RESISTANCE = 2000;
const EDGE_RESISTANCE = 0.85;
// Lower = easier swipes; 0.15 means ~15% of a page-step commits the advance.
const DRAG_COMMIT_THRESHOLD = 0.15;
const DISABLED_CLASS = "is--disabled";

const CSS_VAR_ITEMS_PER_PAGE = "--news-items-per-page";
const DEFAULT_ITEMS_PER_PAGE = 3;

export default function (element: HTMLElement) {
  const prevBtn = element.querySelector<HTMLElement>(
    '[news-slider="arrow-prev"]',
  );
  const nextBtn = element.querySelector<HTMLElement>(
    '[news-slider="arrow-next"]',
  );
  const wrapper = element.querySelector<HTMLElement>(
    '[news-slider="cms-list-wrapper"]',
  );
  const list = element.querySelector<HTMLElement>(
    '[news-slider="cms-list"]',
  );
  const items = Array.from(
    element.querySelectorAll<HTMLElement>('[news-slider="cms-item"]'),
  );

  if (!wrapper || !list || items.length === 0) return;

  let currentPage = 0;
  let pageCount = 1;
  let itemsPerPage = DEFAULT_ITEMS_PER_PAGE;
  let snapPoints: number[] = [0];
  let drag: Draggable | null = null;
  // Page index at drag start; snap fn computes delta relative to this, not the live x.
  let dragStartPage = 0;

  function readCssVarNumber(target: HTMLElement, name: string, fallback: number) {
    const raw = getComputedStyle(target).getPropertyValue(name);
    const v = parseFloat(raw);
    return Number.isFinite(v) && v > 0 ? v : fallback;
  }

  function computeLayout() {
    itemsPerPage = readCssVarNumber(
      element,
      CSS_VAR_ITEMS_PER_PAGE,
      DEFAULT_ITEMS_PER_PAGE,
    );

    pageCount = Math.max(1, Math.ceil(items.length / itemsPerPage));
    // Snap to each page's first item's real laid-out position (offsetLeft is
    // immune to the GSAP x transform), so the step is exact for any layout —
    // desktop hero+thumb grid or tablet one-card-per-page — rather than guessing
    // clientWidth + gap, which drifts by the grid container's padding each page.
    const base = items[0]?.offsetLeft ?? 0;
    snapPoints = Array.from({ length: pageCount }, (_, p) => {
      let left = Infinity;
      for (let k = 0; k < itemsPerPage; k++) {
        const it = items[p * itemsPerPage + k];
        if (it) left = Math.min(left, it.offsetLeft);
      }
      return Number.isFinite(left) ? -(left - base) : 0;
    });

    if (currentPage > pageCount - 1) currentPage = pageCount - 1;
  }

  const xForPage = (page: number) => snapPoints[page] ?? 0;

  function nearestPage(x: number) {
    let best = 0;
    let bestDist = Infinity;
    for (let i = 0; i < snapPoints.length; i++) {
      const d = Math.abs(snapPoints[i]! - x);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    }
    return best;
  }

  function updateArrows() {
    const atStart = currentPage === 0;
    const atEnd = currentPage >= pageCount - 1;
    if (prevBtn) {
      prevBtn.classList.toggle(DISABLED_CLASS, atStart);
      prevBtn.setAttribute("aria-disabled", String(atStart));
    }
    if (nextBtn) {
      nextBtn.classList.toggle(DISABLED_CLASS, atEnd);
      nextBtn.setAttribute("aria-disabled", String(atEnd));
    }
  }

  function applyBounds() {
    if (!drag) return;
    const minX = snapPoints[snapPoints.length - 1] ?? 0;
    drag.applyBounds({ minX, maxX: 0 });
  }

  function render(animate: boolean) {
    const target = xForPage(currentPage);
    if (animate && !reduced) {
      gsap.to(list, {
        x: target,
        duration: TWEEN_DURATION,
        ease: TWEEN_EASE,
        onUpdate: () => drag?.update(),
      });
    } else {
      gsap.set(list, { x: target });
      drag?.update();
    }
    updateArrows();
  }

  function goTo(page: number) {
    const next = Math.max(0, Math.min(pageCount - 1, page));
    if (next === currentPage) return;
    currentPage = next;
    render(true);
  }

  onMount(() => {
    gsap.set(wrapper, { position: "relative" });

    if (prevBtn) prevBtn.style.cursor = "pointer";
    if (nextBtn) nextBtn.style.cursor = "pointer";

    computeLayout();
    render(false);

    const onPrev = () => goTo(currentPage - 1);
    const onNext = () => goTo(currentPage + 1);
    prevBtn?.addEventListener("click", onPrev);
    nextBtn?.addEventListener("click", onNext);

    list.style.cursor = "grab";
    list.style.touchAction = "pan-y"; // vertical scroll still works on touch

    drag = Draggable.create(list, {
      type: "x",
      inertia: true,
      edgeResistance: EDGE_RESISTANCE,
      dragResistance: DRAG_RESISTANCE,
      throwResistance: THROW_RESISTANCE,
      maxDuration: SNAP_DURATION,
      bounds: { minX: snapPoints[snapPoints.length - 1] ?? 0, maxX: 0 },
      snap: (value: number) => {
        const step = snapPoints.length > 1 ? -(snapPoints[1] ?? 0) : 0;
        if (step <= 0) return snapPoints[0] ?? 0;
        const fromX = snapPoints[dragStartPage] ?? 0;
        const delta = fromX - value; // positive = dragged left (toward next page)
        const ratio = delta / step;
        let shift = 0;
        if (Math.abs(ratio) > DRAG_COMMIT_THRESHOLD) {
          shift = Math.sign(ratio) * Math.max(1, Math.round(Math.abs(ratio)));
        }
        const targetPage = Math.max(
          0,
          Math.min(pageCount - 1, dragStartPage + shift),
        );
        return snapPoints[targetPage] ?? 0;
      },
      onPress(this: Draggable) {
        dragStartPage = currentPage;
        gsap.killTweensOf(list);
        (this.target as HTMLElement).style.cursor = "grabbing";
      },
      onRelease(this: Draggable) {
        (this.target as HTMLElement).style.cursor = "grab";
      },
      onThrowUpdate(this: Draggable) {
        const page = nearestPage(this.x);
        if (page !== currentPage) {
          currentPage = page;
          updateArrows();
        }
      },
      onThrowComplete(this: Draggable) {
        currentPage = nearestPage(this.x);
        updateArrows();
      },
    })[0]!;

    const unsubResize = Resize.add(() => {
      // Anchor to the item we're on so a breakpoint change (3-up ⇄ 1-up, where a
      // "page" means a different item count) keeps your place instead of jumping.
      const anchorItem = currentPage * itemsPerPage;
      computeLayout();
      currentPage = Math.min(
        pageCount - 1,
        Math.floor(anchorItem / itemsPerPage),
      );
      gsap.killTweensOf(list);
      gsap.set(list, { x: xForPage(currentPage) });
      drag?.update(); // resync Draggable's x to the new transform, else next drag jumps
      applyBounds();
      updateArrows();
    });

    onDestroy(() => {
      prevBtn?.removeEventListener("click", onPrev);
      nextBtn?.removeEventListener("click", onNext);
      drag?.kill();
      drag = null;
      unsubResize();
      gsap.killTweensOf(list);
      gsap.set([wrapper, list], { clearProps: "all" });
      list.style.cursor = "";
      list.style.touchAction = "";
      [prevBtn, nextBtn].forEach((btn) => {
        if (!btn) return;
        btn.classList.remove(DISABLED_CLASS);
        btn.removeAttribute("aria-disabled");
        btn.style.cursor = "";
      });
    });
  });
}
